import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/useToast';

export interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  connectionType?: string;
  effectiveType?: string;
}

export function useNetworkStatus() {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isSlowConnection: false,
  });
  const { toast } = useToast();

  useEffect(() => {
    const updateNetworkStatus = () => {
      const connection = (navigator as any).connection || 
                        (navigator as any).mozConnection || 
                        (navigator as any).webkitConnection;

      const isOnline = navigator.onLine;
      const isSlowConnection = connection ? 
        connection.effectiveType === 'slow-2g' || 
        connection.effectiveType === '2g' : false;

      setNetworkStatus({
        isOnline,
        isSlowConnection,
        connectionType: connection?.type,
        effectiveType: connection?.effectiveType,
      });

      // Show toast notifications for network changes
      if (!isOnline) {
        toast({
          variant: 'destructive',
          title: 'Connection Lost',
          description: 'You are currently offline. Some features may not work.',
        });
      } else if (isSlowConnection) {
        toast({
          variant: 'warning',
          title: 'Slow Connection',
          description: 'Your connection is slow. Some features may take longer to load.',
        });
      }
    };

    const handleOnline = () => {
      updateNetworkStatus();
      toast({
        variant: 'success',
        title: 'Connection Restored',
        description: 'You are back online.',
      });
    };

    const handleOffline = () => {
      updateNetworkStatus();
    };

    const handleConnectionChange = () => {
      updateNetworkStatus();
    };

    // Initial status
    updateNetworkStatus();

    // Event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [toast]);

  return networkStatus;
}

// Hook for retry logic with network awareness
export function useRetryWithNetwork<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number;
    baseDelay?: number;
    backoffMultiplier?: number;
    retryOnNetworkError?: boolean;
  } = {}
) {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    backoffMultiplier = 2,
    retryOnNetworkError = true,
  } = options;

  const networkStatus = useNetworkStatus();
  const { toast } = useToast();

  const retry = async (): Promise<T> => {
    let lastError: Error;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Don't attempt if offline and it's a network-dependent operation
        if (!networkStatus.isOnline && retryOnNetworkError) {
          throw new Error('No network connection available');
        }

        return await operation();
      } catch (error) {
        lastError = error as Error;
        
        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          toast({
            variant: 'destructive',
            title: 'Operation Failed',
            description: `Failed after ${maxRetries + 1} attempts: ${lastError.message}`,
          });
          throw lastError;
        }
        
        // Calculate delay with exponential backoff
        const delay = baseDelay * Math.pow(backoffMultiplier, attempt);
        
        // Show retry notification
        toast({
          variant: 'warning',
          title: 'Retrying...',
          description: `Attempt ${attempt + 1} failed. Retrying in ${delay / 1000}s...`,
        });
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError!;
  };

  return { retry, networkStatus };
}